
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface OpenAIModel {
  id: string;
  object: string;
  created: number;
  owned_by: string;
}

export interface ChatMessage {
  role: 'system' | 'user' | 'assistant' | 'function' | 'tool';
  content: string | null;
  name?: string;
  function_call?: FunctionCall;
  tool_calls?: ToolCall[];
  tool_call_id?: string;
}

export interface FunctionCall {
  name: string;
  arguments: string;
}

export interface ToolCall {
  id: string;
  type: 'function';
  function: FunctionCall;
}

export interface ChatCompletionOptions {
  model?: string;
  temperature?: number;
  max_tokens?: number;
  stream?: boolean;
  tools?: Tool[];
  tool_choice?: 'auto' | 'none' | ToolChoice;
}

export interface Tool {
  type: 'function';
  function: {
    name: string;
    description: string;
    parameters: Record<string, any>;
  };
}

export interface ToolChoice {
  type: 'function';
  function: {
    name: string;
  };
}

export interface ImageGenerationOptions {
  model?: string;
  size?: '1024x1024' | '1792x1024' | '1024x1792';
  quality?: 'standard' | 'hd';
  n?: number;
}

export interface ImageGenerationResult {
  created: number;
  data: Array<{
    url: string;
    revised_prompt?: string;
  }>;
}

/**
 * Fetch available OpenAI models
 */
export const fetchOpenAIModels = async (): Promise<OpenAIModel[]> => {
  try {
    const { data, error } = await supabase.functions.invoke('get-openai-models');
    
    if (error) {
      console.error('Error fetching OpenAI models:', error);
      toast.error('Failed to fetch OpenAI models');
      throw error;
    }
    
    return data.models;
  } catch (error) {
    console.error('Error in fetchOpenAIModels:', error);
    toast.error('Failed to fetch OpenAI models');
    throw error;
  }
};

/**
 * Generate a chat completion using OpenAI
 */
export const generateChatCompletion = async (
  messages: ChatMessage[],
  options: ChatCompletionOptions = {}
) => {
  try {
    const requestBody: Record<string, any> = {
      messages,
      model: options.model || 'gpt-4o-mini',
      temperature: options.temperature || 0.7,
      max_tokens: options.max_tokens,
      stream: options.stream || false,
    };
    
    // Add tools if provided
    if (options.tools && options.tools.length > 0) {
      requestBody.tools = options.tools;
    }
    
    // Add tool_choice if provided
    if (options.tool_choice) {
      requestBody.tool_choice = options.tool_choice;
    }
    
    const { data, error } = await supabase.functions.invoke('openai-chat-completion', {
      body: requestBody,
    });
    
    if (error) {
      console.error('Error generating chat completion:', error);
      toast.error('Failed to generate chat completion');
      throw error;
    }
    
    return data;
  } catch (error) {
    console.error('Error in generateChatCompletion:', error);
    toast.error('Failed to generate chat completion');
    throw error;
  }
};

/**
 * Generate an image using OpenAI DALL-E
 */
export const generateImage = async (
  prompt: string,
  options: ImageGenerationOptions = {}
): Promise<ImageGenerationResult> => {
  try {
    const { data, error } = await supabase.functions.invoke('openai-image-generation', {
      body: {
        prompt,
        model: options.model || 'dall-e-3',
        size: options.size || '1024x1024',
        quality: options.quality || 'standard',
        n: options.n || 1,
      },
    });
    
    if (error) {
      console.error('Error generating image:', error);
      toast.error('Failed to generate image');
      throw error;
    }
    
    return data;
  } catch (error) {
    console.error('Error in generateImage:', error);
    toast.error('Failed to generate image');
    throw error;
  }
};

/**
 * Stream a chat completion using OpenAI
 * This returns a stream that can be processed by the caller
 */
export const streamChatCompletion = async (
  messages: ChatMessage[],
  options: ChatCompletionOptions = {}
) => {
  try {
    // Properly construct the edge function URL using environment variable instead of protected property
    const functionPath = 'openai-chat-completion';
    
    // Use the SUPABASE_URL from client.ts which is accessible
    // Extract this from the src/integrations/supabase/client.ts file which has the URL
    const supabaseUrl = "https://dysiqkicxwiusdzijtct.supabase.co";
    const functionUrl = `${supabaseUrl}/functions/v1/${functionPath}`;
    
    // Get the current session
    const sessionResponse = await supabase.auth.getSession();
    const session = sessionResponse.data.session;
    
    const requestBody: Record<string, any> = {
      messages,
      model: options.model || 'gpt-4o-mini',
      temperature: options.temperature || 0.7,
      max_tokens: options.max_tokens,
      stream: true,
    };
    
    // Add tools if provided
    if (options.tools && options.tools.length > 0) {
      requestBody.tools = options.tools;
    }
    
    // Add tool_choice if provided
    if (options.tool_choice) {
      requestBody.tool_choice = options.tool_choice;
    }
    
    const response = await fetch(
      functionUrl,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token || ''}`,
        },
        body: JSON.stringify(requestBody),
      }
    );
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('Error streaming chat completion:', errorText);
      toast.error('Failed to stream chat completion');
      throw new Error(errorText);
    }
    
    return response.body;
  } catch (error) {
    console.error('Error in streamChatCompletion:', error);
    toast.error('Failed to stream chat completion');
    throw error;
  }
};

/**
 * Execute a capability through OpenAI function calling
 */
export const executeCapability = async (
  systemPrompt: string,
  userPrompt: string,
  capability: Tool,
  options: Omit<ChatCompletionOptions, 'tools' | 'tool_choice'> = {}
) => {
  try {
    const messages: ChatMessage[] = [
      { role: 'system', content: systemPrompt },
      { role: 'user', content: userPrompt }
    ];

    const result = await generateChatCompletion(messages, {
      ...options,
      tools: [capability],
      tool_choice: { type: 'function', function: { name: capability.function.name } }
    });

    // Extract and process the tool call
    const toolCalls = result.choices[0]?.message?.tool_calls;
    if (toolCalls && toolCalls.length > 0) {
      const toolCall = toolCalls[0];
      // Parse the arguments from the tool call
      const args = JSON.parse(toolCall.function.arguments);
      
      // Execute the capability function via edge function
      const { data, error } = await supabase.functions.invoke('execute-capability', {
        body: {
          capability: capability.function.name,
          arguments: args
        }
      });
      
      if (error) throw error;
      
      // Send the tool response back to the model
      const toolResponse: ChatMessage = {
        role: 'tool',
        content: JSON.stringify(data),
        tool_call_id: toolCall.id
      };
      
      // Get the final response incorporating the tool result
      const finalMessages = [...messages, result.choices[0].message, toolResponse];
      const finalResult = await generateChatCompletion(finalMessages, options);
      
      return {
        initialResponse: result,
        toolExecution: data,
        finalResponse: finalResult
      };
    }
    
    // If no tool call was made, just return the initial response
    return { initialResponse: result };
  } catch (error) {
    console.error('Error executing capability:', error);
    toast.error('Failed to execute capability');
    throw error;
  }
};
